// Agent-Tender enum choices.
//
// Wherever the existing club-tender flow already defines a catalog we REUSE it
// (D-005: "src/constants/tenderChoices.js is the truth, not the bot whitelist").
// Only agent-tender-specific groupings live here.
import {
  PLAYER_POSITIONS,
  STAFF_POSITIONS,
  COACH_POSITIONS,
  CURRENCY_CHOICES,
  SALARY_TYPE_CHOICES,
  MARKET_VALUE_CHOICES,
  FOOT_CHOICES,
  EXPERIENCE_CHOICES,
  RECRUITING_FOR_CHOICES,
  GAMES_PLAYED_CHOICES,
} from "./tenderChoices";

// Re-export the shared catalogs so agent-tender code has a single import site.
export {
  CURRENCY_CHOICES,
  SALARY_TYPE_CHOICES,
  MARKET_VALUE_CHOICES,
  FOOT_CHOICES,
  EXPERIENCE_CHOICES,
  RECRUITING_FOR_CHOICES,
  GAMES_PLAYED_CHOICES,
};

// Category → which positions apply. Staff = coach roles + non-coach staff roles,
// mirroring the two existing constants (no new position strings invented).
export const AGENT_TENDER_CATEGORY_CHOICES = [
  { value: "player", label: "Player" },
  { value: "staff", label: "Staff" },
];

export const STAFF_AND_COACH_POSITIONS = [...COACH_POSITIONS, ...STAFF_POSITIONS];

export const getPositionsForCategory = (category) =>
  category === "staff" ? STAFF_AND_COACH_POSITIONS : PLAYER_POSITIONS;

// Request type depends on category, exactly like the club create flow:
// player → buy/loan/trial, staff → full_time/contract/temporary.
export const PLAYER_TYPE_OF_TENDER_CHOICES = [
  { value: "buy", label: "Buy" },
  { value: "loan", label: "Loan" },
  { value: "trial", label: "Trial" },
];

export const STAFF_TYPE_OF_TENDER_CHOICES = [
  { value: "full_time", label: "Full Time" },
  { value: "contract", label: "Contract" },
  { value: "temporary", label: "Temporary" },
];

export const getTypeChoicesForCategory = (category) =>
  category === "staff"
    ? STAFF_TYPE_OF_TENDER_CHOICES
    : PLAYER_TYPE_OF_TENDER_CHOICES;

// Staff role catalog — mirrors the backend Staff.STAFF_TYPE_CHOICES 1:1 (the
// platform's existing catalog, not an invented list). A staff request keys on
// this instead of a free-text position (Rule 6); it is the dedup discriminator
// for staff on the backend (compute_fingerprint) and drives the staff card.
export const STAFF_ROLE_CHOICES = [
  { value: "coach", label: "Coach" },
  { value: "ceo", label: "CEO" },
  { value: "technical_director", label: "Technical Director" },
  { value: "sporting_director", label: "Sporting Director" },
  { value: "club_admin", label: "Club Admin" },
  { value: "head_of_scouting", label: "Head of Scouting" },
  { value: "team_manager", label: "Team Manager" },
  { value: "scout", label: "Scout" },
  { value: "medical", label: "Medical" },
  { value: "analysis", label: "Analysis" },
  { value: "operations", label: "Operations" },
  { value: "academy", label: "Academy" },
  { value: "admin", label: "Admin" },
  { value: "other", label: "Other" },
];

// V2 staff-path cleanup (approved 2026-07-21): Club Admin / Admin are no longer
// SELECTABLE staff roles on the create form (the backend rejects them too).
// STAFF_ROLE_CHOICES above stays complete so legacy rows still display a label.
export const AGENT_STAFF_ROLE_CHOICES = STAFF_ROLE_CHOICES.filter(
  (r) => r.value !== "club_admin" && r.value !== "admin",
);

const STAFF_ROLE_LABELS = STAFF_ROLE_CHOICES.reduce((acc, r) => {
  acc[r.value] = r.label;
  return acc;
}, {});

// Human label for a staff_role value; falls back to snake_case → Title Case.
// For role "other", the custom title (other_staff_position) wins over the
// literal "Other" — parity with the PDF/email role_display (F1).
export const formatStaffRole = (value, otherTitle) => {
  if (!value) return "";
  if (value === "other" && otherTitle) return otherTitle;
  if (STAFF_ROLE_LABELS[value]) return STAFF_ROLE_LABELS[value];
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ── Create-flow: TWO independent axes (Brief PART B two-axis model) ──────────
// The agent makes two decisions, not one:
//   1. SUBJECT — Player vs Staff (backend `tender_category`). Player keys on a
//      position; Staff keys on a role from STAFF_ROLE_CHOICES.
//   2. LEVEL   — Professional vs Youth (backend `recruiting_for`). This is now
//      REQUIRED for BOTH subjects (a staff hire carries its level too), so a
//      youth academy coach is Subject=Staff + Level=Youth.
// `women` is intentionally NOT offered on either axis — it is not live on the
// agent side and the feed never surfaces it (parity with the feed tabs below).
export const AGENT_SUBJECT_PLAYER = "player";
export const AGENT_SUBJECT_STAFF = "staff";

export const AGENT_SUBJECT_CHOICES = [
  { value: AGENT_SUBJECT_PLAYER, label: "Player" },
  { value: AGENT_SUBJECT_STAFF, label: "Staff" },
];

export const AGENT_LEVEL_PROFESSIONAL = "professional";
export const AGENT_LEVEL_YOUTH = "youth";

export const AGENT_LEVEL_CHOICES = [
  { value: AGENT_LEVEL_PROFESSIONAL, label: "Professional" },
  { value: AGENT_LEVEL_YOUTH, label: "Youth" },
];

// Normalize a stored recruiting_for into one of the two valid levels, defaulting
// to Professional (used to seed the Level dropdown in create + edit prefill —
// a legacy staff row with an empty recruiting_for becomes Professional, never
// blank, so the now-required field is always populated).
export const normalizeAgentLevel = (recruiting_for) =>
  recruiting_for === AGENT_LEVEL_YOUTH
    ? AGENT_LEVEL_YOUTH
    : AGENT_LEVEL_PROFESSIONAL;

// Map the two-axis selection onto the backend field pair the payload sends.
// Staff now sends recruiting_for too (Part B) — it is NEVER cleared. Position vs
// staff_role is decided by the subject; the level is decided independently.
export const subjectLevelToFields = (subject, level) => ({
  tender_category: subject === AGENT_SUBJECT_STAFF ? "staff" : "player",
  recruiting_for: normalizeAgentLevel(level),
});

// Feed category pills (P1) — the WHAT axis (Players/Coaches/Staffs/Others).
// They map server-side onto tender_category + staff_role groups via the shared
// `category` param (backend build_agent_feed_queryset), so the club-side N2 feed
// inherits them 1:1. `women` is excluded by the backend and never a pill.
// Counts come from the feed response `tab_counts` (keyed by these pill values).
export const CATEGORY_PLAYERS = "players";
export const CATEGORY_COACHES = "coaches";
export const CATEGORY_STAFFS = "staffs";
export const CATEGORY_OTHERS = "others";

export const CATEGORY_PILLS = [
  { key: CATEGORY_PLAYERS, label: "Players" },
  { key: CATEGORY_COACHES, label: "Coaches" },
  { key: CATEGORY_STAFFS, label: "Staffs" },
  { key: CATEGORY_OTHERS, label: "Others" },
];

// staff_role → pill grouping. Mirrors the backend category map exactly
// (Lead-specified: `analysis` + `academy` belong to STAFFS, not OTHERS).
// Kept here so any client-side grouping stays consistent with the server.
export const STAFF_ROLE_GROUPS = {
  coaches: ["coach"],
  staffs: [
    "sporting_director",
    "technical_director",
    "head_of_scouting",
    "team_manager",
    "scout",
    "analysis",
    "academy",
  ],
  others: ["ceo", "club_admin", "medical", "operations", "admin", "other"],
};

// Professional / Youth level dropdown for the feed toolbar (recruiting_for).
// Players-only axis, applied additively on top of the category pill; `women`
// is not offered (backend excludes it everywhere).
export const FEED_LEVEL_CHOICES = [
  { value: "", label: "All levels" },
  { value: "professional", label: "Professional" },
  { value: "youth", label: "Youth" },
];

// ── V2 create-form field catalogs (mirror the backend Tender model enums) ────
// Staff start date: Immediately / From month (+ staff_start_month YYYY-MM) /
// Not specified. Staff path only; players carry target_window instead.
export const STAFF_START_TYPE_CHOICES = [
  { value: "immediately", label: "Immediately" },
  { value: "from_month", label: "From month" },
  { value: "not_specified", label: "Not specified" },
];

// Unit for temporary_duration (staff, Temporary type).
export const DURATION_UNIT_CHOICES = [
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

// Display label for a target-window key — client mirror of the backend's
// target_window_label ('summer_2026' → 'Summer 26', 'winter_2026_27' →
// 'Winter 26/27') so the form/preview never drift from cards/PDFs/emails.
const TARGET_WINDOW_RE = /^(summer_(20\d{2})|winter_(20\d{2})_(\d{2}))$/;

export const formatTargetWindow = (value) => {
  const m = TARGET_WINDOW_RE.exec(value || "");
  if (!m) return "";
  if (value.startsWith("summer_")) return `Summer ${Number(m[2]) % 100}`;
  return `Winter ${Number(m[3]) % 100}/${m[4]}`;
};

// Generic upcoming half-year windows — client mirror of the backend's
// _generic_window_seq (same keys, same convention: Summer Y runs Jun-Sep Y,
// Winter Y/Y+1 runs Dec Y - Feb Y+1). Used as the window selector's fallback
// when the window-choices endpoint has not answered (yet); the endpoint
// returns the SAME keys, optionally enriched with real open/close dates.
export const genericWindowChoices = (count = 4, today = new Date()) => {
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  let kind;
  let y;
  if (month <= 2) {
    kind = "winter";
    y = year - 1; // winter (Y-1)/Y still open
  } else if (month <= 9) {
    kind = "summer";
    y = year;
  } else {
    kind = "winter";
    y = year;
  }
  const seq = [];
  while (seq.length < count) {
    if (kind === "summer") {
      seq.push({ value: `summer_${y}` });
      kind = "winter"; // winter Y/Y+1 follows summer Y
    } else {
      seq.push({ value: `winter_${y}_${String((y + 1) % 100).padStart(2, "0")}` });
      kind = "summer";
      y += 1;
    }
  }
  return seq.map((c) => ({
    ...c,
    label: formatTargetWindow(c.value),
    opens_at: null,
    closes_at: null,
  }));
};

// Verification status (trust axis) — mirrors the backend verification_status.
export const VERIFICATION_STATUS = {
  UNVERIFIED: "unverified",
  CLUB_VERIFIED: "club_verified",
  SUPERSEDED: "superseded",
  REVOKED_FRAUD: "revoked_fraud",
};

// Lifecycle status (separate axis) — mirrors the existing Tender.status.
export const LIFECYCLE_STATUS = {
  ACTIVE: "active",
  DEAL: "deal",
  EXPIRED: "expired",
};

// Response (pitch) status — a response is a Pitch on a source=agent tender, so
// this mirrors the existing Pitch.status values. NOTE: a new, un-actioned
// response is "active" (the Pitch default); there is no "pending" value.
export const RESPONSE_STATUS = {
  ACTIVE: "active",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
};

// Tabs on the "My agent requests" page. All = own tenders; Responses/Shortlist/
// Declined are views over the responses received on those tenders; Expired is a
// lifecycle filter of the own tenders.
export const MY_REQUESTS_TABS = [
  { key: "all", label: "All" },
  { key: "responses", label: "Responses" },
  { key: "shortlist", label: "Shortlist" },
  { key: "expired", label: "Expired" },
  { key: "declined", label: "Declined" },
];

// Feed filter option lists (static v1; a backend filters endpoint can replace
// these later without touching the UI).
export const FEED_TYPE_FILTER_CHOICES = [
  { value: "", label: "All types" },
  { value: "buy", label: "Buy" },
  { value: "loan", label: "Loan" },
  { value: "trial", label: "Trial" },
  { value: "full_time", label: "Full Time" },
  { value: "contract", label: "Contract" },
  { value: "temporary", label: "Temporary" },
];

export const FEED_CATEGORY_FILTER_CHOICES = [
  { value: "", label: "All" },
  { value: "player", label: "Player" },
  { value: "staff", label: "Staff" },
];

export const LEAGUE_TIER_FILTER_CHOICES = [
  { value: "", label: "All tiers" },
  { value: "1", label: "1st Tier" },
  { value: "2", label: "2nd Tier" },
  { value: "3", label: "3rd Tier" },
  { value: "4", label: "4th Tier" },
];

// Full feed-filter option lists for parity with the club TenderFilterModal
// (G4 in API-CONTRACT). Params use the exact same keys as the club feed so the
// backend can share query handling.
export const FEED_RECRUITING_FOR_CHOICES = [
  { value: "", label: "All" },
  { value: "professional", label: "Professional" },
  { value: "youth", label: "Youth" },
  { value: "women", label: "Women" },
];

export const FEED_STATUS_FILTER_CHOICES = [
  { value: "all", label: "All status" },
  { value: "active", label: "Active" },
  { value: "deal", label: "Deal" },
  { value: "expired", label: "Expired" },
];

// Sort values match the backend `sort` param (P8): newest (default) | oldest |
// window_soonest. Verified tenders are ALWAYS shown first regardless of sort —
// the sort only sets the tiebreaker among equally-verified rows.
export const FEED_SORT_CHOICES = [
  { value: "newest", label: "Newest first" },
  { value: "window_soonest", label: "Transfer window (soonest)" },
  { value: "oldest", label: "Oldest first" },
];

// Default feed filter state — the full club-parity param set (G4). Shared by
// the feed hook and the filter modal so they never drift.
export const DEFAULT_FEED_FILTERS = {
  tender_category: "",
  market_value_from: "",
  market_value_to: "",
  continent: "",
  country: "",
  division: "",
  position: "",
  nationality: "",
  min_age: "",
  max_age: "",
  type_of_tender: "",
  recruiting_for: "",
  status: "all",
  sort_by: "",
  verified: false,
};

// The six continents, matching the club modal's static fallback list.
export const CONTINENT_CHOICES = [
  { value: "", label: "All continents" },
  { value: "Africa", label: "Africa" },
  { value: "Asia", label: "Asia" },
  { value: "Europe", label: "Europe" },
  { value: "North America", label: "North America" },
  { value: "Oceania", label: "Oceania" },
  { value: "South America", label: "South America" },
];

// Human labels for arbitrary db position values (covers both player + staff).
const ALL_POSITION_LABELS = [
  ...PLAYER_POSITIONS,
  ...STAFF_AND_COACH_POSITIONS,
].reduce((acc, p) => {
  acc[p.value] = p.label;
  return acc;
}, {});

export const formatPositionValue = (value, otherStaffPosition = "") => {
  if (!value) return otherStaffPosition || "";
  if (ALL_POSITION_LABELS[value]) return ALL_POSITION_LABELS[value];
  // Fallback: snake_case → Title Case.
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// Title for any request payload: staff requests title with the staff role
// (custom title for "other"), player requests with the position (F9 — the N2
// club feed serves staff requests via the identical feed serializer).
export const formatRequestTitle = (t) =>
  t?.tender_category === "staff"
    ? formatStaffRole(t.staff_role, t.other_staff_position)
    : formatPositionValue(t?.position, t?.other_staff_position);

export const formatTypeOfTender = (value) => {
  if (!value) return "";
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const formatLeagueTier = (tier) => {
  const n = Number(tier);
  if (!n) return "";
  if (n === 1) return "1st Tier";
  if (n === 2) return "2nd Tier";
  if (n === 3) return "3rd Tier";
  return `${n}th Tier`;
};

// One transfer-window date → "1 Jul 2026", parsed as UTC so a date-only
// string ("2026-07-01") never drifts a day across timezones. Mirrors the
// UTC handling in components/home/TransferWindow.js. Null on missing/invalid.
const formatWindowDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getUTCDate();
  const month = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  const year = d.getUTCFullYear();
  return `${day} ${month} ${year}`;
};

// Open/close date line from the window's opens_at / closes_at. Both bounds
// are optional (an open-ended window has neither); returns null when there
// is no date to show at all so the caller can fall back to the label.
export const formatWindowDateRange = (opensAt, closesAt) => {
  const a = formatWindowDate(opensAt);
  const b = formatWindowDate(closesAt);
  if (a && b) return `${a} - ${b}`;
  if (a) return `Opens ${a}`;
  if (b) return `Closes ${b}`;
  return null;
};

// Normalize a tender's transfer_window into { label, dates } for display.
// Tolerant of BOTH shapes the backend can send:
//   • legacy string ("Summer 2026")            → { label, dates: null }
//   • object { label, opens_at, closes_at }    → label + formatted date line
// Returns null only when there is nothing at all to render.
export const formatTransferWindow = (transferWindow) => {
  if (!transferWindow) return null;
  if (typeof transferWindow === "string") {
    const t = transferWindow.trim();
    return t ? { label: t, dates: null } : null;
  }
  if (typeof transferWindow === "object") {
    const rawLabel = transferWindow.label;
    const label =
      typeof rawLabel === "string" && rawLabel.trim() ? rawLabel.trim() : null;
    const dates = formatWindowDateRange(
      transferWindow.opens_at,
      transferWindow.closes_at,
    );
    if (!label && !dates) return null;
    return { label, dates };
  }
  return null;
};
